import { getBasePath } from '../utils/url-utils';
import type { ClientEnvironment } from 'bc-model';
import { createStore } from 'zustand/vanilla';

type Nullable<T> = { [K in keyof T]: T[K] | null };

type ClientEnvironmentValue = boolean | number | string | string[] | null | undefined;

const initialEnvState: Nullable<ClientEnvironment> = {
  arcGisServerUrl: null,
  accountLinkingEnabled: null,
  authenticationProviders: null,
  bcApiUrl: null,
  bcApiExtUrl: null,
  prosemirrorStepDebounceTime: null,
  prosemirrorStepMaxDebounceTime: null,
  apiCacheTTL: null,
  combinedArtifactsLoadEnabled: null,
  metricsAdapters: null,
  timeSeriesMetricAdapters: null,
  faroEnabled: null,
  faroTracingEnabled: null,
  faroPerfInstrumentationEnabled: null,
  version: null,
  showUnclassifiedDisclaimer: null,
  systemClassificationBannerPre: '',
  systemClassificationBannerMark: null,
  systemClassificationBannerPost: '',
  expiredLinkHostPromptEnabled: null,
  expiredLinkHosts: null,
  pendingTransactionMonitorInterval: null,
  pendingTransactionStuckThreshold: null,
  memLogMinWaitMs: null,
  memLogMaxWaitMs: null,
};

const envStore = createStore<Nullable<ClientEnvironment>>(() => ({
  ...initialEnvState,
}));

let loadStatus: 'idle' | 'pending' | 'complete' | 'failed' = 'idle';
let loadPromise: Promise<boolean> | null = null;
let retries = 0;
const MAX_RETRIES = 5;

const areEnvironmentVariablesLoaded = (): boolean => {
  return loadStatus === 'complete';
};

const reloadEnvironmentVariables = async (): Promise<boolean> => {
  if (!areEnvironmentVariablesLoaded()) {
    try {
      const response = await fetch(`${getBasePath()}/api/env`);
      envStore.setState(await response.json());
      loadStatus = 'complete';
    } catch (err) {
      /**
       * Fetching via relative URLs doesn't work in vitest, so we should
       * only log failures in non-test environments.
       */
      if (typeof process !== 'undefined' && process?.env?.VITEST !== 'true') {
        console.warn('Could not load environment variables', err);
      }
      loadStatus = 'failed';

      return false;
    }
  }
  return true;
};

const getEnvironmentVariable = async <Key extends keyof ClientEnvironment>(
  key: Key
): Promise<ClientEnvironment[Key] | null> => {
  if (loadStatus !== 'complete' && loadStatus !== 'pending') {
    loadStatus = 'pending';
    loadPromise = reloadEnvironmentVariables();
  }

  if (loadPromise) {
    await loadPromise;
    loadPromise = null;
  }

  if (areEnvironmentVariablesLoaded()) return envStore.getState()[key] ?? null;

  retries += 1;
  if (retries >= MAX_RETRIES) {
    console.warn(`Max retries reached. Failed to load environment variables`);
    return null;
  }
  return getEnvironmentVariable(key);
};

const getAllEnvironmentVariables = async (): Promise<ClientEnvironment> => {
  if (loadStatus !== 'complete' && loadStatus !== 'pending') {
    loadStatus = 'pending';
    loadPromise = reloadEnvironmentVariables();
  }

  if (loadPromise) {
    await loadPromise;
    loadPromise = null;
  }

  if (areEnvironmentVariablesLoaded()) return envStore.getState() as ClientEnvironment;

  retries += 1;
  if (retries >= MAX_RETRIES) {
    console.warn(`Max retries reached. Failed to load environment variables`);
    return envStore.getState() as ClientEnvironment;
  }
  return getAllEnvironmentVariables();
};

const getCachedEnvironmentVariable = <T extends ClientEnvironmentValue>(
  key: keyof ClientEnvironment,
  defaultValue: T
): T => {
  const value = envStore.getState()[key];
  return (value as T) ?? defaultValue;
};

const TESTING_INTERNAL_ENV: { reset: () => void } = {
  reset: () => {
    loadStatus = 'idle';
    loadPromise = null;
    envStore.setState(initialEnvState, true);
  },
};

export {
  areEnvironmentVariablesLoaded,
  reloadEnvironmentVariables,
  getAllEnvironmentVariables,
  getEnvironmentVariable,
  getCachedEnvironmentVariable,
  MAX_RETRIES,
  TESTING_INTERNAL_ENV,
};
